import { useState } from 'react'
import './style.css'
import UserBookModal from '../../UserBookModal'
import { IS_REF_MOBILE } from '../../../utils/refViewport'
import { bookRatios, BookRatio } from '../bookRatio'

// Reference `v-kval` directive (MATCH_ODDS sizes): >= 1000 -> one-decimal K.
const kval = (value) => {
  const n = Number(value)
  if (n >= 1e3) return `${parseFloat((n / 1e3).toFixed(1))}K`
  return parseFloat(value)
}

// Reference `v-kvalsportmax` / `v-kvalsportmin` directives (Bookmaker sizes, Min/Max). The
// reference divides by brateSport * bratemulSport (both 1 for the admin panel).
const kvalsportmax = (value) => {
  const n = Number(Number(value).toFixed(2))
  if (n >= 1e3 && n < 1e5) return `${parseFloat((n / 1e3).toFixed(2))}K`
  if (n >= 1e5) return `${parseFloat((n / 1e5).toFixed(2))}L`
  return parseFloat(n)
}

// Raw feed market (get-match-private-data) -> view model, sorted like the reference (sno).
function rawView(m) {
  const sections = [...(m.section || [])]
    .sort((a, b) => (a.sno ?? 0) - (b.sno ?? 0))
    .map((s) => ({
      nat: s.nat,
      gstatus: s.gstatus,
      gscode: s.gscode,
      odds: (s.odds || []).map((o) => ({ otype: String(o.otype || '').toLowerCase(), tno: o.tno, odds: o.odds, size: o.size })),
    }))
  return {
    mname: m.mname,
    gtype: m.gtype,
    status: m.status,
    gscode: m.gscode,
    min: m.min,
    max: m.max,
    maxBet: m.iplay ? m.maxb : m.umaxbof,
    rem: m.rem,
    sections,
  }
}

// Transformed marketData ({team1, team2, team3}) -> view model, used until the page passes the
// raw market. Bookmaker odds were divided by 100 upstream (convertOdds) — undo it for display.
function fallbackView(data, marketName, displayTitle, isYesNo) {
  const isBookmaker = String(marketName || '').toLowerCase().includes('bookmaker')
  const fixOdds = (v) => (isBookmaker && v && !Number.isInteger(v) ? Math.round(v * 100) : v)
  const tiers = isYesNo ? [0] : [0, 1, 2]
  const sections = [data.team1, data.team2, data.team3].filter((t) => t && t.name).map((t) => ({
    nat: t.name,
    gstatus: 'ACTIVE',
    odds: tiers.flatMap((tno) => [
      { otype: 'back', tno, odds: fixOdds(t.odds?.[tno]?.back || 0), size: t.amounts?.[tno]?.back || 0 },
      { otype: 'lay', tno, odds: fixOdds(t.odds?.[tno]?.lay || 0), size: t.amounts?.[tno]?.lay || 0 },
    ]),
  }))
  return {
    mname: isBookmaker && displayTitle.toLowerCase() === 'bookmaker' ? 'Bookmaker' : displayTitle,
    gtype: isBookmaker ? 'match1' : 'match',
    status: 'OPEN',
    gscode: 1,
    min: null,
    max: null,
    maxBet: 1,
    rem: '',
    sections,
  }
}

/**
 * MatchOdds Component
 *
 * Displays betting odds table for different market types.
 * Shows data passed from props.
 * 
 * @param {string} title - Market title (optional, defaults to marketName)
 * @param {string} marketName - Selected market name (e.g., "MATCH_ODDS", "OVER_UNDER_25")
 * @param {Object} marketData - Market data object with team1 and team2 data
 * @param {Function} onBetLockClick - Callback for Bet Lock button
 */
function MatchOdds({ title, marketName, marketData, market, index, onBetLockClick, exposureData = {}, eventId }) {
  const [showUserBook, setShowUserBook] = useState(false)
  // Reference Market4 flashes a box bg-success / bg-danger when its odds moved up / down since
  // the previous feed (marketDataOld). Keep the previous raw market as derived state.
  const [feed, setFeed] = useState({ cur: market, old: null })
  if (market !== feed.cur) setFeed({ cur: market, old: feed.cur })
  const oldView = feed.old ? rawView(feed.old) : null
  const marketIndex = index ?? (String(marketName || '').toLowerCase().includes('bookmaker') ? 1 : 0)

  // Default data fallback if marketData is not provided
  const defaultData = {
    team1: { name: 'Victoria', odds: [{ back: 1.74, lay: 2.06 }, { back: 1.9, lay: 2.12 }, { back: 1.94, lay: 2.34 }], amounts: [{ back: 1, lay: 2 }, { back: 83.02, lay: 11.31 }, { back: 2.07, lay: 185.45 }] },
    team2: { name: 'Western Australia', odds: [{ back: 1.74, lay: 2.06 }, { back: 1.9, lay: 2.12 }, { back: 1.94, lay: 2.86 }], amounts: [{ back: 250.46, lay: 1.95 }, { back: 12.62, lay: 74.4 }, { back: 2.12, lay: 152.03 }] }
  }

  // Determine display title
  const displayTitle = title || marketName || 'MATCH_ODDS'
  
  // Use marketData from props or fallback to default
  const data = marketData || defaultData

  const nameLower = (marketName || '').toLowerCase()
  const isShowUserBook = nameLower.includes('match odds') || nameLower.includes('match_odds') || nameLower.includes('bookmaker')
  
  const runners = []
  if (data.team1?.name) runners.push(data.team1.name)
  if (data.team2?.name) runners.push(data.team2.name)
  if (data.team3?.name) runners.push(data.team3.name)
  if (data.items) data.items.forEach(item => { if (item.label) runners.push(item.label) })

  // Check if this is HT/FT market or multi-column market (special layout)
  const isHTFT = data.type === 'htft' || marketName === 'HT/FT'
  const isMultiColumn = data.type === 'multi-column'

  // Robust detection for Yes/No or 2-outcome markets
  const isYesNo = (() => {
    // 1. Check for exactly 2 runners (Team 1 and Team 2 exist, Team 3 doesn't)
    const hasOnlyTwoRunners = !!data.team1?.name && !!data.team2?.name && !data.team3?.name
    if (!hasOnlyTwoRunners) return false

    // 2. Check for explicit "Yes" and "No" runners
    const t1Name = data.team1.name.toLowerCase()
    const t2Name = data.team2.name.toLowerCase()
    if ((t1Name === 'yes' && t2Name === 'no') || (t1Name === 'no' && t2Name === 'yes')) return true

    // 3. Check for specific 2-outcome market names
    const nameLower = (marketName || '').toLowerCase()
    const isTwoOutcomeMarket = nameLower.includes('both teams to score') || 
                               nameLower.includes('over_under') ||
                               nameLower.includes('over/under')
    
    return isTwoOutcomeMarket
  })()

  // Render HT/FT or multi-column layout
  if ((isHTFT || isMultiColumn) && data.items) {
    return (
      <div className="market-1">
        <div className="bet-table">
          <div className="bet-table-header">
            <span title={displayTitle} data-toggle="collapse" data-target="#market0">
              {displayTitle}
            </span>
            <div className="float-right">
              <span className="max-bet">
                <span title="Max : 1">1</span>
              </span>
              <a 
                href="javascript:void(0)" 
                className="btn btn-back"
                onClick={(e) => {
                  e.preventDefault()
                  if (onBetLockClick) {
                    onBetLockClick(marketName)
                  }
                }}
              >
                Bet Lock
              </a>
              {isShowUserBook && (
                <a 
                  href="javascript:void(0)" 
                  className="btn btn-back"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowUserBook(true)
                  }}
                >
                  User Book
                </a>
              )}
            </div>
          </div>
          <div id="market0" data-title="OPEN" className="bet-table-body collapse show">
            <div className="bet-table-row">
              {data.items.map((item, index) => (
                <div key={index} className={`market-1-item ${item.isSuspended ? 'suspended' : ''}`}>
                  <div>
                    <span>{item.label}</span>
                    <span className={`ml-2 ${exposureData[item.label] >= 0 ? 'text-green' : 'text-red'}`}>
                      {exposureData[item.label] || 0}
                    </span>
                    <span className="ml-2 d-none">0</span>
                  </div>
                  <div className={`bl-box back back ${item.isSuspended ? 'no-val suspended' : ''}`}>
                    <span className="d-block odds">
                      {item.isSuspended || item.odds === null ? '—' : item.odds}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <UserBookModal 
          show={showUserBook} 
          onHide={() => setShowUserBook(false)} 
          matchId={eventId} 
          marketName={marketName}
          initialRunners={runners}
        />
      </div>
    )
  }

  // Standard layout — reference Market4 template (market-4 > bet-table), MATCH_ODDS (gtype
  // "match") and Bookmaker (gtype "match1"). Rendered from the raw feed market when the page
  // passes it (`market`), otherwise from the transformed marketData.
  const view = market ? rawView(market) : fallbackView(data, marketName, displayTitle, isYesNo)
  const isMatch1 = view.gtype === 'match1'
  const showUserBook_ = view.gtype === 'match' || view.gtype === 'match1'
  const boxClass = (side, o, i) => {
    const old = oldView?.sections?.[i]?.odds?.find((x) => x.otype === side && x.tno === o.tno)
    const prev = old ? old.odds : o.odds
    const flash = prev < o.odds ? ' bg-success' : prev > o.odds ? ' bg-danger' : ''
    const tier = side === 'back' ? ['back', 'back1', 'back2'][o.tno] : ['lay', 'lay1', 'lay2'][o.tno]
    return `bl-box ${side}${tier ? ` ${tier}` : ''}${Number(o.odds) === 0 ? ' no-val' : ''}${flash}`
  }
  const sizeText = (size) => (isMatch1 ? kvalsportmax(size) : kval(size))
  const maxBet = isMatch1 ? (
    <span className="max-bet">
      {'\n                        Min:'}<span>{view.min == null ? '' : kvalsportmax(view.min)}</span>{' \n                        Max:'}<span>{view.max == null ? '' : kvalsportmax(view.max)}</span>
    </span>
  ) : view.gtype === 'match' ? (
    <span className="max-bet">
      <span title={`Max : ${kvalsportmax(view.maxBet)}`}>{kvalsportmax(view.maxBet)}</span>
    </span>
  ) : (
    <span className="max-bet">Max:<span>{kvalsportmax(view.maxBet)}</span></span>
  )

  return (
    <div className="market-4">
      <div className="bet-table">
        <div className="bet-table-header">
          <div className="nation-name" data-toggle="collapse" data-target={`#market${marketIndex}`} aria-expanded="true">
            <span title={view.mname}>{`\n                    ${view.mname}\n                `}</span>
          </div>{' '}
          <div className="float-right">
            <a
              href="javascript:void(0)"
              className="btn btn-back"
              onClick={(e) => {
                e.preventDefault()
                if (onBetLockClick) {
                  onBetLockClick(marketName)
                }
              }}
            >
              Bet Lock
            </a>{' '}
            {showUserBook_ && isShowUserBook && (
              <a
                href="javascript:void(0)"
                className="btn btn-back"
                onClick={(e) => {
                  e.preventDefault()
                  setShowUserBook(true)
                }}
              >
                User Book
              </a>
            )}
          </div>
        </div>{' '}
        <div
          id={`market${marketIndex}`}
          data-title={view.status}
          className={`bet-table-body collapse show${view.gscode != null && Number(view.gscode) !== 1 ? ' suspendedfull' : ''}`}
        >
          {IS_REF_MOBILE ? (
            <div className="text-right d-inline-block w-100 pr-2">{maxBet}</div>
          ) : (
            <div className="bet-table-row bet-table-row-top">
              <div className="text-right nation-name">{maxBet}</div>{' '}
              <div className="back bl-title d-none-mobile">Back</div>{' '}
              <div className="lay bl-title d-none-mobile">Lay</div>
            </div>
          )}{' '}
          {view.sections.map((section, i) => {
            const book = Number(exposureData[section.nat]) || 0
            const ratio = bookRatios(view.sections.map((s) => exposureData[s.nat]))[i]
            const backs = section.odds.filter((o) => o.otype === 'back').sort((a, b) => b.tno - a.tno)
            const lays = section.odds.filter((o) => o.otype === 'lay').sort((a, b) => a.tno - b.tno)
            const suspendedText = Number(view.gscode) === 1 && section.gscode != null && Number(section.gscode) !== 1
            return (
              <div key={`web${i}`} data-title={section.gstatus} className={`bet-table-row${suspendedText ? ' suspendedtext' : ''}`}>
                <div className="nation-name d-none-mobile">
                  <p>{section.nat}</p>{' '}
                  <p className="mb-0 float-left" style={{ color: book > 0 ? 'green' : 'red' }}>
                    {exposureData[section.nat] || 0}
                    <BookRatio value={ratio} />
                  </p>{' '}
                  <p className="mb-0 float-right d-none">0</p>
                </div>{' '}
                {backs.map((o) => (
                  <div key={`backodds${o.tno}`} className={boxClass('back', o, i)}>
                    <span className="d-block odds">{Number(o.odds) !== 0 ? o.odds : '\u2014'}</span>{' '}
                    {Number(o.size) !== 0 && <span className="d-block">{sizeText(o.size)}</span>}
                  </div>
                ))}{' '}
                {lays.map((o) => (
                  <div key={`layodds${o.tno}`} className={boxClass('lay', o, i)}>
                    <span className="d-block odds">{Number(o.odds) !== 0 ? o.odds : '\u2014'}</span>{' '}
                    {Number(o.size) !== 0 && <span className="d-block">{sizeText(o.size)}</span>}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>{' '}
      {view.rem ? <small className="remark">{view.rem}</small> : null}
      <UserBookModal
        show={showUserBook}
        onHide={() => setShowUserBook(false)}
        matchId={eventId}
        marketName={marketName}
        initialRunners={runners}
      />
    </div>
  )
}

export default MatchOdds
